import { getEnv } from '@repo/utils';
import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

// Function to extract tenant from hostname
const getTenantFromHostname = (): string | null => {
  const { hostname } = window.location;

  // Skip localhost and IP addresses
  if (hostname === 'localhost' || /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/.test(hostname)) {
    return null;
  }
  // Extract first subdomain as tenant
  const parts = hostname.split('.');
  return parts.length > 2 ? parts[0]! : null;
};

Sentry.init({
  dsn: 'https://7a24e0a430ceb8a98b3f967fe9ec2b39@sentry.scaler.com/12',
  environment: getEnv('MODE'),
  integrations: [
    // See docs for support of different versions of variation of react router
    // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    // Mask-by-default: every lead-facing screen renders names, phone numbers and emails, so
    // replay must not opt those out. No element in this app is marked `.sentry-unmask` /
    // `.sentry-unblock`, so nothing bypasses these defaults.
    Sentry.replayIntegration({
      maskAllText: true,
      maskAllInputs: true,
      blockAllMedia: true,
    }),
  ],
  // Do not send chunk load errors to Sentry
  beforeSend: (event) => {
    // Check if this error was handled in the ErrorBoundary
    const tags = event.tags || {};
    if (tags.handled_in_boundary === 'true') {
      return null;
    }

    const tenant = getTenantFromHostname();
    if (tenant) {
      event.tags = { ...event.tags, tenant };
    }

    return event;
  },
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for tracing.
  tracesSampleRate: 0.2,

  // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
  tracePropagationTargets: [],

  // Capture Replay for 10% of all sessions,
  // plus for 100% of sessions with an error
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});
